import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { database } from '../../firebaseConfig';
import { FiEdit, FiPhone, FiMapPin, FiUser, FiBarChart2, FiBook, FiCheck, FiX, FiSave } from 'react-icons/fi';
import { IoEarthOutline } from 'react-icons/io5';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../PageLayout';
import SEO from '../SEO';
import LoadingSpinner from '../loader2';
import { useTheme } from '../../context/ThemeContext';

const countries = [
  { label: 'United States', value: 'US', flag: 'us' },
  { label: 'Canada', value: 'CA', flag: 'ca' },
  { label: 'India', value: 'IN', flag: 'in' },
  { label: 'Germany', value: 'DE', flag: 'de' },
  { label: 'Australia', value: 'AU', flag: 'au' },
  { label: 'United Kingdom', value: 'GB', flag: 'gb' },
  { label: 'France', value: 'FR', flag: 'fr' },
  { label: 'Italy', value: 'IT', flag: 'it' },
  { label: 'Japan', value: 'JP', flag: 'jp' },
  { label: 'Brazil', value: 'BR', flag: 'br' },
  { label: 'South Korea', value: 'KR', flag: 'kr' },
  { label: 'Mexico', value: 'MX', flag: 'mx' },
  { label: 'Spain', value: 'ES', flag: 'es' },
  { label: 'Russia', value: 'RU', flag: 'ru' },
  { label: 'China', value: 'CN', flag: 'cn' },
  { label: 'South Africa', value: 'ZA', flag: 'za' },
  { label: 'Pakistan', value: 'PK', flag: 'pk' },
  { label: 'Saudi Arabia', value: 'SA', flag: 'sa' },
  { label: 'United Arab Emirates', value: 'AE', flag: 'ae' },
  { label: 'Qatar', value: 'QA', flag: 'qa' },
  { label: 'Kuwait', value: 'KW', flag: 'kw' },
  { label: 'Bahrain', value: 'BH', flag: 'bh' },
  { label: 'Oman', value: 'OM', flag: 'om' },
];

// Preparing for options
const preparingForOptions = [
  { label: 'Engineering / Computer Science / BS Mathematics (With Chemistry)', value: 'engineering_cs_math_chemistry' },
  { label: 'Engineering / Computer Science / BS Mathematics (With Computer Science)', value: 'engineering_cs_math_cs' },
  { label: 'BS Biotechnology / BS Environmental Science', value: 'biotech_env_science' },
  { label: 'Business Studies & Social Sciences', value: 'business_social_sciences' },
  { label: 'Bachelor of Architecture / Industrial Design', value: 'architecture_industrial_design' },
  { label: 'BS Mathematics', value: 'bs_mathematics' },
];

const MyVibes = ({ user }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [preparingFor, setPreparingFor] = useState(null);
  const [attemptedQuestions, setAttemptedQuestions] = useState([]);
  const [editable, setEditable] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        if (!user || !user.uid) {
          setError('User not authenticated');
          setIsLoading(false);
          return;
        }

        const userRef = doc(database, 'users', user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();

          if (userData.personalInfo) {
            const { name, city, country, phoneNumber, preparingFor } = userData.personalInfo;
            setName(name || '');
            setCity(city || '');
            setPhoneNumber(phoneNumber || '');
            setSelectedCountry(countries.find((c) => c.value === country) || null);
            setPreparingFor(preparingForOptions.find((p) => p.value === preparingFor) || null);
          }

          if (userData.attempted_questions) {
            setAttemptedQuestions(userData.attempted_questions || []);
          }
        } else {
          setError('User document not found.');
        }
      } catch (err) {
        setError('Error loading user profile data.');
        console.error('Error loading user profile:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      loadUserProfile();
    }
  }, [user]);

  const handleSaveChanges = async () => {
    try {
      if (!user || !user.uid) return;
      setIsLoading(true);
      const userRef = doc(database, 'users', user.uid);
      await updateDoc(userRef, {
        personalInfo: {
          name,
          city,
          country: selectedCountry?.value,
          phoneNumber,
          preparingFor: preparingFor?.value,
        },
      });
      setEditable(false);
    } catch (err) {
      setError('Error saving changes.');
      console.error('Error saving user profile:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const totalAttempted = attemptedQuestions.length;

  // Theme-based classes
  const cardClass = isDark
    ? 'bg-dark-secondary/50 border border-white/10'
    : 'bg-white border border-gray-200';

  const inputClass = isDark
    ? 'bg-dark-tertiary/50 border-white/10 text-white placeholder-gray-500'
    : 'bg-white border-gray-300 text-gray-900';

  const textClass = isDark ? 'text-white' : 'text-gray-800';
  const subTextClass = isDark ? 'text-gray-400' : 'text-gray-600';
  const labelClass = isDark ? 'text-gray-300' : 'text-gray-700';

  if (error) {
    return (
      <PageLayout userName={name}>
        <div className="flex items-center justify-center p-4 min-h-[60vh]">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={`p-8 rounded-2xl shadow-2xl max-w-md w-full ${cardClass}`}
          >
            <div className="text-red-500 text-center text-xl font-semibold mb-4">{error}</div>
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:opacity-90 transition-all font-medium shadow-lg"
            >
              Back to Dashboard
            </button>
          </motion.div>
        </div>
      </PageLayout>
    );
  }

  if (isLoading) {
    return (
      <PageLayout userName={name}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner />
        </div>
      </PageLayout>
    );
  }

  return (
    <>
      <SEO
        title="My Profile - Prepify | NUST NET Preparation Dashboard"
        description="Manage your Prepify profile and track your NUST Entry Test preparation progress."
        keywords="Prepify Profile, NUST Preparation Dashboard, NET Progress Tracker"
        url="/profile"
      />
      <PageLayout userName={name}>
        <div className="container mx-auto py-8 px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`rounded-2xl overflow-hidden shadow-xl ${cardClass}`}
          >
            {/* Profile Header */}
            <div className="bg-gradient-to-br from-purple-600 via-violet-600 to-pink-500 p-8 text-white relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
              </div>

              <div className="relative z-10 text-center">
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="w-24 h-24 mx-auto bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center shadow-xl ring-4 ring-white/30 mb-4"
                >
                  <FiUser className="text-white text-4xl" />
                </motion.div>
                <h2 className="text-3xl font-bold mb-2">My Profile</h2>
                <p className="text-purple-100 text-sm italic">"Success is the sum of small efforts, repeated day in and day out."</p>
              </div>
            </div>

            {/* Profile Content */}
            <div className="p-6">
              <div className="grid gap-4 md:grid-cols-2">
                {/* Name */}
                <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-tertiary/30' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                      <FiUser className="text-white text-lg" />
                    </div>
                    <div className="flex-grow">
                      <span className={`block text-xs font-medium ${labelClass} mb-1`}>Name</span>
                      {editable ? (
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className={`w-full text-sm rounded-lg p-2 border ${inputClass}`}
                          placeholder="Enter your name"
                        />
                      ) : (
                        <span className={`block text-sm font-semibold ${textClass}`}>{name || 'Not set'}</span>
                      )}
                    </div>
                    <button onClick={() => setEditable(!editable)} className="p-1.5 rounded-lg hover:bg-white/10">
                      <FiEdit className="text-purple-500 text-lg" />
                    </button>
                  </div>
                </div>

                {/* City */}
                <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-tertiary/30' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg">
                      <FiMapPin className="text-white text-lg" />
                    </div>
                    <div className="flex-grow">
                      <span className={`block text-xs font-medium ${labelClass} mb-1`}>City</span>
                      {editable ? (
                        <input
                          type="text"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          className={`w-full text-sm rounded-lg p-2 border ${inputClass}`}
                          placeholder="Enter your city"
                        />
                      ) : (
                        <span className={`block text-sm font-semibold ${textClass}`}>{city || 'Not set'}</span>
                      )}
                    </div>
                    <button onClick={() => setEditable(!editable)} className="p-1.5 rounded-lg hover:bg-white/10">
                      <FiEdit className="text-blue-500 text-lg" />
                    </button>
                  </div>
                </div>

                {/* Country */}
                <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-tertiary/30' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
                      <IoEarthOutline className="text-white text-lg" />
                    </div>
                    <div className="flex-grow">
                      <span className={`block text-xs font-medium ${labelClass} mb-1`}>Country</span>
                      {editable ? (
                        <select
                          value={selectedCountry?.value || ''}
                          onChange={(e) => setSelectedCountry(countries.find((c) => c.value === e.target.value))}
                          className={`w-full text-sm rounded-lg p-2 border ${inputClass}`}
                        >
                          <option value="">Select a country</option>
                          {countries.map((country) => (
                            <option key={country.value} value={country.value}>{country.label}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`block text-sm font-semibold ${textClass}`}>{selectedCountry?.label || 'Not set'}</span>
                      )}
                    </div>
                    <button onClick={() => setEditable(!editable)} className="p-1.5 rounded-lg hover:bg-white/10">
                      <FiEdit className="text-green-500 text-lg" />
                    </button>
                  </div>
                </div>

                {/* Phone */}
                <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-tertiary/30' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg">
                      <FiPhone className="text-white text-lg" />
                    </div>
                    <div className="flex-grow">
                      <span className={`block text-xs font-medium ${labelClass} mb-1`}>Phone</span>
                      {editable ? (
                        <input
                          type="text"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          className={`w-full text-sm rounded-lg p-2 border ${inputClass}`}
                          placeholder="Enter phone number"
                        />
                      ) : (
                        <span className={`block text-sm font-semibold ${textClass}`}>{phoneNumber || 'Not set'}</span>
                      )}
                    </div>
                    <button onClick={() => setEditable(!editable)} className="p-1.5 rounded-lg hover:bg-white/10">
                      <FiEdit className="text-orange-500 text-lg" />
                    </button>
                  </div>
                </div>

                {/* Preparing For - Full width */}
                <div className={`rounded-xl p-4 md:col-span-2 ${isDark ? 'bg-dark-tertiary/30' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-500 rounded-lg">
                      <FiBook className="text-white text-lg" />
                    </div>
                    <div className="flex-grow">
                      <span className={`block text-xs font-medium ${labelClass} mb-1`}>Preparing For</span>
                      {editable ? (
                        <select
                          value={preparingFor?.value || ''}
                          onChange={(e) => setPreparingFor(preparingForOptions.find((p) => p.value === e.target.value) || null)}
                          className={`w-full text-sm rounded-lg p-2 border ${inputClass}`}
                        >
                          <option value="">Select what you are preparing for</option>
                          {preparingForOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`block text-sm font-semibold ${textClass}`}>{preparingFor?.label || 'Not set'}</span>
                      )}
                    </div>
                    <button onClick={() => setEditable(!editable)} className="p-1.5 rounded-lg hover:bg-white/10">
                      <FiEdit className="text-violet-500 text-lg" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-6 bg-gradient-to-br from-purple-600 via-violet-600 to-pink-500 rounded-xl p-6 text-white"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl">
                      <FiBarChart2 className="text-white text-2xl" />
                    </div>
                    <div>
                      <span className="block text-sm text-purple-100 mb-1">Total MCQs Attempted</span>
                      <span className="text-3xl font-bold">{totalAttempted || 0}</span>
                      <span className="text-purple-200 text-sm ml-2">questions</span>
                    </div>
                  </div>
                  <div className="px-4 py-2 bg-white/20 backdrop-blur-md rounded-lg">
                    <span className="text-sm font-medium">Keep going! 🚀</span>
                  </div>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <AnimatePresence>
                {editable && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-6 space-y-3"
                  >
                    <button
                      onClick={handleSaveChanges}
                      className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:opacity-90 transition-all font-semibold flex items-center justify-center gap-2 shadow-lg"
                    >
                      <FiSave className="text-lg" />
                      Save Changes
                    </button>
                    <button
                      onClick={() => setEditable(false)}
                      className={`w-full py-3 rounded-xl transition-all font-medium flex items-center justify-center gap-2 ${isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      <FiX className="text-lg" />
                      Cancel
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                onClick={() => navigate('/')}
                className="w-full mt-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:opacity-90 transition-all font-semibold shadow-lg"
              >
                Back to Dashboard
              </button>
            </div>
          </motion.div>
        </div>
      </PageLayout>
    </>
  );
};

export default MyVibes;